import {
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import type { InvoiceConceptTemplateVersion } from "../invoicing/invoicingTypes";
import { parseDecimal } from "../invoicing/invoicingScreenRules";
import { APP_LOCALE, NUMERIC_DATE_FORMAT } from "../misc/locale";
import type { CrmDataProvider } from "../providers/types";
import type { Project } from "../types";

const MAX_PCT = 100;
const PCT_DECIMALS = 4;
const PLACEHOLDERS = [
  "{N}",
  "{PCT}",
  "{UNIT}",
  "{CLIENT}",
  "{ADVISOR}",
  "{PROJECT}",
].join(" ");
// A project has a handful of versions: one bounded page is the whole history.
const VERSIONS_PAGE_SIZE = 100;

/** Runs a project RPC, notifies and refetches; resolves to the error message (Spanish, raised server-side) or null. */
const useProjectRun = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  return async (
    action: (provider: CrmDataProvider) => Promise<void>,
    doneKey: string,
  ): Promise<string | null> => {
    try {
      await action(dataProvider);
      notify(doneKey, { type: "success" });
      refresh();
      return null;
    } catch (error) {
      return error instanceof Error && error.message
        ? error.message
        : translate("crm.operations.error");
    }
  };
};

const CommissionSection = ({ project }: { project: Project }) => {
  const translate = useTranslate();
  const run = useProjectRun();
  const [text, setText] = useState(
    project.developer_commission_pct == null
      ? ""
      : String(project.developer_commission_pct),
  );
  const [error, setError] = useState<string | null>(null);
  const pct = parseDecimal(text, PCT_DECIMALS);
  const isValid = pct != null && pct <= MAX_PCT;

  return (
    <div className="space-y-2">
      <Label htmlFor="project-developer-commission">
        {translate("crm.projects.invoicing.commission_label")}
      </Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id="project-developer-commission"
          inputMode="decimal"
          className="h-11 w-32 sm:h-9"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <Button
          type="button"
          variant="gradient"
          className="h-11 sm:h-9"
          disabled={
            !isValid || pct === (project.developer_commission_pct ?? null)
          }
          onClick={async () => {
            if (pct == null) return;
            setError(
              await run(
                (provider) =>
                  provider.setProjectDeveloperCommission(project.id, pct),
                "crm.projects.invoicing.commission_saved",
              ),
            );
          }}
        >
          {translate("crm.projects.invoicing.commission_save")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {translate("crm.projects.invoicing.commission_hint")}
      </p>
      {error ? (
        <p role="alert" className="text-sm text-[#B3321C]">
          {error}
        </p>
      ) : null}
    </div>
  );
};

const TemplateHistory = ({ projectId }: { projectId: Project["id"] }) => {
  const translate = useTranslate();
  const { data } = useGetList<InvoiceConceptTemplateVersion>(
    "invoice_concept_template_versions",
    {
      filter: { project_id: projectId },
      pagination: { page: 1, perPage: VERSIONS_PAGE_SIZE },
      sort: { field: "version", order: "DESC" },
    },
  );
  if (!data || data.length === 0) return null;
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">
        {translate("crm.projects.invoicing.history_title")}
      </h4>
      <ul className="space-y-2" data-testid="template-history">
        {data.map((row) => (
          <li key={row.id} className="rounded-md border p-3 text-sm">
            <p className="text-xs text-muted-foreground">
              {translate("crm.projects.invoicing.version_n", {
                n: row.version,
              })}{" "}
              ·{" "}
              {new Date(row.created_at).toLocaleDateString(
                APP_LOCALE,
                NUMERIC_DATE_FORMAT,
              )}
            </p>
            <p className="break-words">{row.template}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TemplateSection = ({ project }: { project: Project }) => {
  const translate = useTranslate();
  const run = useProjectRun();
  const [template, setTemplate] = useState(project.invoice_concept_template);
  const [error, setError] = useState<string | null>(null);
  const trimmed = template.trim();

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label htmlFor="project-invoice-template">
          {translate("crm.projects.invoicing.template_label")}
        </Label>
        <span
          data-testid="template-version"
          className="text-sm text-muted-foreground tabular-nums"
        >
          {translate("crm.projects.invoicing.version_n", {
            n: project.invoice_concept_template_version,
          })}
        </span>
      </div>
      <Textarea
        id="project-invoice-template"
        rows={4}
        value={template}
        onChange={(event) => setTemplate(event.target.value)}
      />
      <p className="text-xs text-muted-foreground">
        {translate("crm.projects.invoicing.placeholders_help", {
          placeholders: PLACEHOLDERS,
        })}
      </p>
      <p className="text-xs text-muted-foreground">
        {translate("crm.projects.invoicing.frozen_warning")}
      </p>
      <Button
        type="button"
        variant="gradient"
        className="h-11 sm:h-9"
        disabled={
          trimmed === "" || trimmed === project.invoice_concept_template.trim()
        }
        onClick={async () =>
          setError(
            await run(
              (provider) =>
                provider.setProjectInvoiceTemplate(project.id, trimmed),
              "crm.projects.invoicing.template_saved",
            ),
          )
        }
      >
        {translate("crm.projects.invoicing.template_save")}
      </Button>
      {error ? (
        <p role="alert" className="text-sm text-[#B3321C]">
          {error}
        </p>
      ) : null}
      <TemplateHistory projectId={project.id} />
    </div>
  );
};

/**
 * Almikia Epic E6 (TASK-041): invoicing settings of a project, direccion only
 * (the page is already gated). The developer commission % and the invoice
 * concept template are changed through their RPCs (a template change appends
 * a version; receivables already approved keep their frozen text), never
 * through the generic project form. The version number is read-only.
 */
export const ProjectInvoicingSettings = () => {
  const project = useRecordContext<Project>();
  const translate = useTranslate();
  if (!project) return null;
  return (
    <Card>
      <CardContent className="space-y-6 pt-4">
        <h3 className="font-semibold">
          {translate("crm.projects.invoicing.title")}
        </h3>
        <CommissionSection
          key={`pct-${project.developer_commission_pct}`}
          project={project}
        />
        <TemplateSection
          key={`tpl-${project.invoice_concept_template_version}`}
          project={project}
        />
      </CardContent>
    </Card>
  );
};
